import { type ComponentPropsWithoutRef, forwardRef, memo } from 'react';

import { Command } from '@air/cmdk';
import { ImageSearchIcon } from '@air/next-icons';
import { cn } from '@air/utils-tailwind';

import { usePrivateSearchUIHelpers } from '~/components/Search/hooks/usePrivateSearchUIHelpers';
import { type SearchCommandFilterCountProps } from '~/components/SearchCommand/SearchCommandFilterCount';
import { SearchCommandInputPlaceholder } from '~/components/SearchCommand/SearchCommandInputPlaceholder';
import { SearchCommandModeSelector } from '~/components/SearchCommand/SearchCommandModeSelector';
import { useAppliedFiltersCount } from '~/hooks/filters/useAppliedFiltersCount';

export type SmartSearchCommandInputProps = Pick<
  ComponentPropsWithoutRef<typeof Command.Input>,
  'className' | 'defaultValue' | 'onValueChange' | 'placeholder' | 'value'
> &
  Partial<Pick<SearchCommandFilterCountProps, 'count'>> & {
    compact?: boolean;
  };

export const SmartSearchCommandInput = memo(
  forwardRef<HTMLInputElement, SmartSearchCommandInputProps>(
    ({ className, compact, defaultValue, onValueChange, placeholder, value }: SmartSearchCommandInputProps, ref) => {
      const { similarToImageSearchAvailable, isActiveImageSearch } = usePrivateSearchUIHelpers();
      const { filtersCanBeSaved } = useAppliedFiltersCount();

      return (
        <div
          className={cn(
            'relative -m-px flex shrink-0 items-center gap-2 overflow-hidden rounded-8 border border-transparent pr-3 pl-2 outline-none',
            compact ? 'h-8' : 'h-10',
            className,
          )}
          data-testid="SEARCH_COMMAND_INPUT"
        >
          <div className="relative order-2 min-w-0 flex-1 self-stretch">
            <Command.Input
              className={cn(
                'size-full rounded-8 font-medium text-grey-12 outline-none',
                compact
                  ? 'bg-transparent pr-10 text-14 placeholder:text-transparent'
                  : 'text-16 placeholder:text-grey-9',
                className,
              )}
              data-testid="SEARCH_COMMAND_INPUT_INPUT"
              defaultValue={defaultValue}
              onValueChange={onValueChange}
              placeholder={placeholder}
              value={value}
              ref={ref}
            />
            {compact && !value && placeholder && (
              <SearchCommandInputPlaceholder className="left-0" placeholder={placeholder} />
            )}
          </div>
          <SearchCommandModeSelector className="order-1 shrink-0 self-center" search={value} />
          {similarToImageSearchAvailable && !value && !isActiveImageSearch && !filtersCanBeSaved && (
            <ImageSearchIcon className="order-3 ml-auto size-4 shrink-0 text-grey-10" />
          )}
        </div>
      );
    },
  ),
);

SmartSearchCommandInput.displayName = 'SmartSearchCommandInput';
